import { Dayjs } from 'dayjs';
import {
  DatedGreeks,
  PersistedVolSkewLine,
  RawGreeksDataMap,
  RawStatsObject,
} from 'types/impliedVol';
import { dayjs, ET, getQueryDate } from 'util/shared/date';

/** Latest queryable trade-session day (stable across weekends/holidays/pre-open). */
export const getSessionDay = (tz: string = ET): string =>
  getQueryDate(true, tz).format('YYYY-MM-DD');

/** Whether a line's trade date is the current session day (a current-greeks line). */
export const isSessionDay = (tradeDate: Dayjs, tz: string = ET): boolean =>
  tradeDate.format('YYYY-MM-DD') === getSessionDay(tz);

/** Persisted line → runtime DatedGreeks skeleton (greeks/stats loaded later). */
export const hydrateLine = (
  line: PersistedVolSkewLine,
  sym: string,
): DatedGreeks => ({
  key: line.key,
  sym,
  tradeDate: dayjs(line.tradeDate),
  expiryDate: line.expiryDate,
  greeksMap: {} as RawGreeksDataMap,
  statsMap: {} as RawStatsObject,
  currentPrice: 0,
  color: line.color,
});

/** Runtime DatedGreeks → URL/DB-safe persisted shape. */
export const serializeLine = (dg: DatedGreeks): PersistedVolSkewLine => ({
  key: dg.key,
  tradeDate: dg.tradeDate.format('YYYY-MM-DD'),
  expiryDate: dg.expiryDate,
  color: dg.color,
});

/** Stable equality on the persisted fields, to gate redundant config writes. */
export const sameLines = (
  a: PersistedVolSkewLine[],
  b: PersistedVolSkewLine[],
): boolean =>
  a.length === b.length &&
  a.every((l, i) => {
    const o = b[i];
    return (
      o != null &&
      l.key === o.key &&
      l.tradeDate === o.tradeDate &&
      l.expiryDate === o.expiryDate &&
      l.color === o.color
    );
  });
